import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaGift, FaPen, FaTrash, FaPercent, FaRupeeSign } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function OffersPage() {
    const [savingOffer, setSavingOffer] = useState(false);
    const [offerData, setOfferData] = useState({ name: '', value: '', unit: '', minAmount: '', isActive: true, isWelcomeOffer: false });
    const [offerList, setOfferList] = useState([]);
    const [filteredOffers, setFilteredOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const handleOfferList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/offerdata`);
            const offers = response.data || [];
            setOfferList(offers);
            setFilteredOffers(offers);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    // Search offers
    const handleSearchOffer = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = offerList.filter(offer => 
                offer.name?.toLowerCase().includes(search) ||
                offer.value?.toString().includes(search) ||
                offer.unit?.toLowerCase().includes(search)
            );
            setFilteredOffers(filtered);
        } else {
            setFilteredOffers(offerList);
        }
    };

    const validateFields = () => {
        if (!offerData.name.trim()) {
            alert('Offer name is required.');
            return false;
        }
        if (!offerData.value.toString().trim() || isNaN(Number(offerData.value))) {
            alert('Offer value must be a valid number.');
            return false;
        }
        if (!offerData.unit.trim()) {
            alert('Offer unit is required.');
            return false;
        }
        if (!offerData.minAmount.toString().trim() || isNaN(Number(offerData.minAmount))) {
            alert('Minimum amount must be a valid number.');
            return false;
        }
        return true;
    };

    const handleAddOffer = async () => {
        if (!validateFields()) return;

        try {
            setSavingOffer(true);
            const response = await axios.post(`${API_BASE_URL}/add-offer`, offerData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            setOfferData({ name: '', value: '', unit: '', minAmount: '', isActive: true, isWelcomeOffer: false });
            handleOfferList();
            // Close modal
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingOffer(false);
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setOfferData({
            ...offerData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleDeleteOffer = async (id) => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/delete-offer/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleOfferList();
        } catch (error) {
            handleApiError(error);
        }
    };

    useEffect(() => {
        handleOfferList();
    }, []);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">OFFER MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage promotional offers and discounts</p>
                            </div>
                            <button 
                                type="button" 
                                className="add-brand-btn"
                                data-bs-toggle="modal" 
                                data-bs-target="#offerModal"
                                aria-label="Add a new offer"
                            >
                                <FaPlus className="icon" />
                                <span className="txt">Add Offer</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Control Panel */}
                <div className="pending-control-panel">
                    <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px' }}>
                        <input
                            type="text"
                            className="form-control search-input-custom w-100"
                            placeholder="Search by offer name, value, or unit..."
                            value={searchTerm}
                            onChange={handleSearchOffer}
                        />
                        <FaSearch className="search-icon-custom" />
                    </div>
                    <div className="d-flex align-items-center">
                        <small className="text-muted fw-semibold">
                            Showing {filteredOffers.length} of {offerList.length} offers
                        </small>
                    </div>
                </div>

                {/* Main Content */}
                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div className="row mt-0">
                        <div className="col-12">
                            {filteredOffers.length > 0 ? (
                                <div className="card order-table-card">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>OFFER NAME</th>
                                                    <th>VALUE</th>
                                                    <th>UNIT</th>
                                                    <th>MIN AMOUNT</th>
                                                    <th>TYPE</th>
                                                    <th>STATUS</th>
                                                    <th className="text-end pe-4">ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredOffers.map((offer, index) => (
                                                    <tr key={offer._id}>
                                                        <td className="fw-semibold text-muted">{index + 1}</td>
                                                        <td className="fw-bold text-dark">{offer.name}</td>
                                                        <td className="fw-bold text-success">
                                                            {offer.value}
                                                        </td>
                                                        <td>
                                                            <span className={`badge rounded-pill ${offer.unit === 'Percent' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-primary-subtle text-primary border border-primary-subtle'} px-2 py-1`}>
                                                                {offer.unit === 'Percent' ? <FaPercent className="me-1" style={{ fontSize: '10px' }} /> : <FaRupeeSign className="me-1" style={{ fontSize: '10px' }} />}
                                                                {offer.unit}
                                                            </span>
                                                        </td>
                                                        <td className="fw-semibold text-secondary">
                                                            ₹{offer.minAmount || 0}
                                                        </td>
                                                        <td>
                                                            {offer.isWelcomeOffer ? (
                                                                <span className="badge rounded-pill bg-info-subtle text-info border border-info-subtle px-2 py-1">Welcome</span>
                                                            ) : (
                                                                <span className="text-muted small">Standard</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {offer.isActive !== false ? (
                                                                <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-2 py-1">Active</span>
                                                            ) : (
                                                                <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">Inactive</span>
                                                            )}
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="order-actions-wrapper justify-content-end">
                                                                <Link
                                                                    to={`/admin/offers/edit/${offer._id}`}
                                                                    className="order-action-btn order-action-btn-view"
                                                                    title="Edit Offer"
                                                                >
                                                                    <FaPen />
                                                                </Link>
                                                                <button
                                                                    type="button"
                                                                    className="order-action-btn order-action-btn-cancel"
                                                                    onClick={() => handleDeleteOffer(offer._id)}
                                                                    title="Delete Offer"
                                                                >
                                                                    <FaTrash />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="card order-table-card">
                                    <div className="card-body text-center py-5">
                                        <FaGift className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No offers found matching your search.' : 'No offers found'}
                                        </h5>
                                        <p className="text-muted mb-0">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first offer to get started.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add Offer Modal - Cloned from Brands Page Modal Design */}
            <div className="modal fade add-brand-modal" id="offerModal" tabIndex={-1} aria-labelledby="offerModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold" id="offerModalLabel">Add New Offer</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Offer Name</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Enter offer name" 
                                    name="name" 
                                    value={offerData.name} 
                                    onChange={handleChange} 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label">Offer Value</label>
                                <input 
                                    type="number" 
                                    min={0} 
                                    className="form-control" 
                                    placeholder="Enter offer value" 
                                    name="value" 
                                    value={offerData.value} 
                                    onChange={handleChange} 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label">Offer Unit</label>
                                <select 
                                    className="form-select form-control" 
                                    name="unit" 
                                    value={offerData.unit} 
                                    onChange={handleChange}
                                >
                                    <option value="">Select unit</option>
                                    <option value="Percent">Percent (%)</option>
                                    <option value="Rupees">Rupees (₹)</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Minimum Amount</label>
                                <input 
                                    type="number" 
                                    min={0} 
                                    className="form-control" 
                                    placeholder="Enter minimum order amount for this offer" 
                                    name="minAmount" 
                                    value={offerData.minAmount} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="mb-3 form-check form-switch d-flex align-items-center">
                                <input 
                                    type="checkbox" 
                                    className="form-check-input me-2" 
                                    id="isWelcomeOffer"
                                    name="isWelcomeOffer" 
                                    checked={offerData.isWelcomeOffer} 
                                    onChange={handleChange} 
                                    style={{ width: '38px', height: '20px', cursor: 'pointer' }}
                                />
                                <label className="form-check-label fw-semibold text-dark mb-0" htmlFor="isWelcomeOffer" style={{ cursor: 'pointer' }}>Is New User Welcome Offer</label>
                            </div>
                            
                            <div className="mb-3 form-check form-switch d-flex align-items-center">
                                <input 
                                    type="checkbox" 
                                    className="form-check-input me-2" 
                                    id="isActive"
                                    name="isActive" 
                                    checked={offerData.isActive} 
                                    onChange={handleChange} 
                                    style={{ width: '38px', height: '20px', cursor: 'pointer' }}
                                />
                                <label className="form-check-label fw-semibold text-dark mb-0" htmlFor="isActive" style={{ cursor: 'pointer' }}>Is Offer Active</label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn type-button btn-light" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="save-brand-btn" 
                                onClick={handleAddOffer} 
                                disabled={savingOffer || !offerData.name.trim() || !offerData.value.toString().trim() || !offerData.unit.trim() || !offerData.minAmount.toString().trim()}
                            >
                                {savingOffer ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Offer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                <div className='mt-5 mb-5 pb-5'></div>
            </div>
        </Fragment>
    );
}

export default OffersPage;
